import {
  type Dispatch,
  type SetStateAction,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { useVirtualizer } from '@tanstack/react-virtual'
import { useUnit } from 'effector-react'

import ArrowLeftIcon from '@/assets/icons/icon-arrow-left.svg'
import { Button } from '@/components/ui/button'
import type { Entry } from '@/components/ui/table/expandable-dnd-table/useTreeTable.ts'
import { Tooltip } from '@/components/ui/tooltip'
import { KeyCodes } from '@/consts/keys.ts'
import { $activeChatDetails } from '@/models/chattingUI/chats/chats.ts'
import {
  $focusedScriptId,
  $scriptFolderItems,
  $scriptItems,
  getItemIdentifier,
  isItemFocused,
  isItemValidFolder,
  resetFocusedScriptId,
  resetScriptsFilter,
  setFocusedScriptId,
  setScriptsOpen,
} from '@/models/scripts/chatting.ts'
import { $vaultMediasMap, loadMultipleMediasFx } from '@/models/vault/medias'
import { useSentScriptIdsByFanIdQuery } from '@/modules/scripts/hooks/useSentScriptIdsByFanIdQuery'
import type { ScriptNode } from '@/modules/scripts/types.ts'
import type { StreamableVaultMedia } from '@/modules/vault/types.ts'
import { classNames } from '@/utils/helpers'
import { getFormattedMessageByMessageObject } from '@/utils/message-formatter.ts'

import { ChatScriptItem } from './chat-script-item'

interface Props {
  setText: (value: string) => void
  onScriptClick: (script: ScriptNode) => void
  setPrice: Dispatch<SetStateAction<number | undefined>>
  setMedias: (medias: StreamableVaultMedia[]) => void
  triggerRef: React.RefObject<HTMLButtonElement>
  setLoadingMediaCount?: Dispatch<SetStateAction<number>>
}

const ChatScriptsComponent = ({
  setLoadingMediaCount,
  setText,
  setPrice,
  setMedias,
  triggerRef,
  onScriptClick,
}: Props) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const activeChat = useUnit($activeChatDetails)
  const vaultMediasMap = useUnit($vaultMediasMap)
  const scriptFolderItems = useUnit($scriptFolderItems)
  const scriptItems = useUnit($scriptItems)
  const focusedScriptId = useUnit($focusedScriptId)

  const parentRef = useRef(null)

  const [selectedFolder, setSelectedFolder] =
    useState<Entry<ScriptNode> | null>(null)
  const { data: sentScriptIds = [] } = useSentScriptIdsByFanIdQuery(
    activeChat?.withUser?.id ?? 0,
  )

  const displayItems = useMemo(() => {
    if (selectedFolder) {
      return selectedFolder.scripts || []
    }

    return [...scriptFolderItems, ...scriptItems]
  }, [selectedFolder, scriptFolderItems, scriptItems])

  const rowVirtualizer = useVirtualizer({
    count: displayItems.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 88,
  })

  const focusableItems = useMemo(() => {
    return displayItems
  }, [displayItems])

  const handleClose = useCallback(() => {
    resetFocusedScriptId()
    resetScriptsFilter()
    setScriptsOpen(false)
  }, [])

  const handleScriptClick = useCallback(
    async (s: ScriptNode) => {
      onScriptClick(s)
      handleClose()
      setMedias([])

      if (activeChat?.withUser) {
        setText(
          getFormattedMessageByMessageObject(s.message, activeChat.withUser) ??
            '',
        )
      } else {
        setText(s.message?.text ?? '')
      }

      setPrice(s.message?.price)

      if (s.message?.medias?.length) {
        const mediaIds = s.message.medias.map(({ mediaId }) => mediaId)
        const missingMediaIds = mediaIds.filter((id) => !vaultMediasMap[id])

        // load info about missing medias
        if (missingMediaIds.length) {
          setLoadingMediaCount?.(mediaIds.length)
          await loadMultipleMediasFx(missingMediaIds)
          // eslint-disable-next-line effector/no-getState
          const mediasMap = $vaultMediasMap.getState()

          setMedias(mediaIds.map((id) => mediasMap[id]!).filter(Boolean))
          setLoadingMediaCount?.(0)
        } else {
          // all media info exists, just add it
          setMedias(mediaIds.map((id) => vaultMediasMap[id]!).filter(Boolean))
        }
      } else {
        setMedias([])
      }
    },
    [
      onScriptClick,
      handleClose,
      setMedias,
      activeChat.withUser,
      setPrice,
      setText,
      vaultMediasMap,
      setLoadingMediaCount,
    ],
  )

  useEffect(() => {
    const handleKeydown = (event: KeyboardEvent) => {
      if (event.code === 'Escape') {
        event.preventDefault()
        event.stopPropagation()

        if (selectedFolder) {
          setSelectedFolder(null)
        } else {
          handleClose()
        }
      }

      if (focusableItems?.length) {
        let highLightedItem

        if (event.code === 'ArrowDown') {
          event.preventDefault()
          const firstItem = focusableItems[0]

          if (focusedScriptId) {
            const itemIndex = focusableItems.findIndex((i) =>
              isItemFocused(i, focusedScriptId),
            )
            const nextItem = focusableItems[itemIndex + 1]

            if (nextItem) {
              const id = getItemIdentifier(nextItem)

              setFocusedScriptId(id)
              highLightedItem = id
            } else {
              const id = getItemIdentifier(firstItem)

              setFocusedScriptId(id)
              highLightedItem = id
            }
          } else {
            const id = getItemIdentifier(firstItem)

            setFocusedScriptId(id)
            highLightedItem = id
          }
        }

        if (event.code === 'ArrowUp') {
          event.preventDefault()
          const lastItem = focusableItems[focusableItems.length - 1]

          if (focusedScriptId) {
            const itemIndex = focusableItems.findIndex((i) =>
              isItemFocused(i, focusedScriptId),
            )
            const nextItem = focusableItems[itemIndex - 1]

            if (nextItem) {
              const id = getItemIdentifier(nextItem)

              setFocusedScriptId(id)
              highLightedItem = id
            } else {
              const id = getItemIdentifier(lastItem)

              setFocusedScriptId(id)
              highLightedItem = id
            }
          } else {
            const id = getItemIdentifier(lastItem)

            setFocusedScriptId(id)
            highLightedItem = id
          }
        }

        if (highLightedItem) {
          document
            .querySelector(`[data-id="${highLightedItem}"]`)
            ?.scrollIntoView({
              block: 'nearest',
            })
        }

        if (focusedScriptId) {
          if (event.code === KeyCodes.ENTER) {
            event.preventDefault()
            event.stopPropagation()

            const item = focusableItems.find((i) =>
              isItemFocused(i, focusedScriptId),
            )

            if (item) {
              if (isItemValidFolder(item)) {
                setSelectedFolder(item)
                setText('')
              } else {
                handleScriptClick(item)
                handleClose()
              }
            }
          }
        }
      }
    }

    const registerTimeout = setTimeout(() => {
      document.addEventListener('keydown', handleKeydown)
    }, 200)

    return () => {
      clearTimeout(registerTimeout)
      document.removeEventListener('keydown', handleKeydown)
    }
  }, [
    focusedScriptId,
    handleScriptClick,
    focusableItems,
    selectedFolder,
    setText,
    handleClose,
  ])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        !triggerRef.current?.contains(event.target as Node)
      ) {
        handleClose() // Call onClose if clicked outside
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [handleClose, triggerRef])

  const handleFolderClick = useCallback((scriptFolder: Entry<ScriptNode>) => {
    setSelectedFolder(scriptFolder)
    resetFocusedScriptId()
  }, [])

  const handleBackClick = useCallback(() => {
    setSelectedFolder(null)
    resetFocusedScriptId()
  }, [])

  const getSentScriptCount = useCallback(
    (folder: Entry<ScriptNode>) => {
      return (
        folder.scripts?.filter((script) => sentScriptIds.includes(script.id))
          .length || 0
      )
    },
    [sentScriptIds],
  )

  const getSentLabelStyles = useCallback(
    (folder: Entry<ScriptNode>) => {
      const sentCount = getSentScriptCount(folder)
      const hasSentScriptNode = sentCount > 0

      if (!hasSentScriptNode) {
        return 'bg-secondary-100 text-secondary-500 dark:bg-secondary-500 dark:text-secondary-100'
      }

      if (sentCount === folder.scripts?.length) {
        return 'bg-danger-dark text-white'
      }

      return 'bg-yellow-500 text-white'
    },
    [getSentScriptCount],
  )

  return (
    <div
      className="max-h-[320px] outline-none"
      ref={containerRef}
      data-testid="scripts-panel"
      tabIndex={-1}
    >
      <div
        className="bg-card flex max-h-[320px] flex-col rounded border outline-none"
        tabIndex={-1}
      >
        {selectedFolder && (
          <div className="border-border flex flex-shrink-0 items-center gap-2 border-b px-2.5 py-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBackClick}
              className="h-8 w-8 p-0"
            >
              <ArrowLeftIcon className="h-4 w-4" />
            </Button>
            <span className="font-semibold text-text-default">
              {selectedFolder.name}
            </span>
          </div>
        )}

        <div
          ref={parentRef}
          className="flex-1 overflow-auto py-2.5"
        >
          {selectedFolder && displayItems.length === 0 ? (
            <div className="flex items-center justify-center py-8 text-center">
              <span className="text-secondary-400">
                No scripts in this folder
              </span>
            </div>
          ) : (
            <div
              className="relative"
              style={{
                height: `${rowVirtualizer.getTotalSize()}px`,
              }}
            >
              {rowVirtualizer.getVirtualItems().map((virtualItem) => {
                const item = displayItems[virtualItem.index]

                if (!item) return null

                if (isItemValidFolder(item)) {
                  const sentCount = getSentScriptCount(item)

                  return (
                    <div
                      key={virtualItem.key}
                      data-index={virtualItem.index}
                      ref={rowVirtualizer.measureElement}
                      className="absolute left-0 top-0 w-full"
                      style={{
                        transform: `translateY(${virtualItem.start}px)`,
                      }}
                    >
                      <div
                        data-id={getItemIdentifier(item)}
                        data-testid="script-folder-item"
                        onClick={() => handleFolderClick(item)}
                        className={classNames(
                          'flex cursor-pointer items-center justify-between px-2.5 py-2 outline-none hover:bg-primary-100 dark:hover:bg-primary-600',
                          {
                            'bg-primary-200 dark:bg-primary-700':
                              focusedScriptId === getItemIdentifier(item),
                          },
                        )}
                        tabIndex={-1}
                      >
                        <span className="font-semibold text-text-default">
                          {item.name}
                        </span>
                        <Tooltip
                          asChild
                          content={`${sentCount} out of ${item.scripts?.length} scripts have been sent to the fan`}
                        >
                          <span
                            className={classNames(
                              'ml-auto rounded-2xl px-1.5 py-0.5 text-xs',
                              getSentLabelStyles(item),
                            )}
                          >
                            {sentCount}/{item.scripts?.length}
                          </span>
                        </Tooltip>
                      </div>
                    </div>
                  )
                }

                return (
                  <div
                    key={virtualItem.key}
                    data-index={virtualItem.index}
                    ref={rowVirtualizer.measureElement}
                    className="absolute left-0 top-0 w-full"
                    style={{
                      transform: `translateY(${virtualItem.start}px)`,
                    }}
                  >
                    <ChatScriptItem
                      script={item}
                      onClick={handleScriptClick}
                      isSent={sentScriptIds.includes(item.id)}
                    />
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export const ChatScripts = memo(ChatScriptsComponent)
